import React from 'react';
import Icon from '../../../components/AppIcon';


const ContentCard = ({ content, viewMode, isFavorite, onToggleFavorite, onPreview }) => {
  const getContentIcon = (contentType) => {
    const icons = {
      email_template: 'Mail',
      ad_copy: 'TrendingUp',
      video_script: 'Video',
      report: 'FileText',
      checklist: 'CheckSquare',
      analytics_report: 'BarChart3'
    };
    return icons?.[contentType] || 'FileText';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getPerformanceColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const thumbnailUrl = content?.metadata?.thumbnail_url || 'https://api.dicebear.com/7.x/shapes/svg?seed=' + content?.id;
  const performanceScore = content?.metadata?.performance_score || 0;
  const industry = content?.metadata?.industry || 'General';
  const sourceUrl = content?.metadata?.source_url;

  if (viewMode === 'list') {
    return (
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 hover:border-primary/50 transition-all duration-250 cursor-pointer"
        onClick={() => onPreview(content)}
      >
        <div className="flex items-start gap-3 sm:gap-4">
          {/* Thumbnail */}
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
            <img
              src={thumbnailUrl}
              alt={`Thumbnail for ${content?.title}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://api.dicebear.com/7.x/shapes/svg?seed=' + content?.id;
              }}
            />
          </div>

          {/* Content Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground text-base sm:text-lg truncate">{content?.title}</h3>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-xs sm:text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name={getContentIcon(content?.content_type)} size={14} />
                    <span className="hidden sm:inline">{content?.content_type?.replace('_', ' ')}</span>
                  </span>
                  <span className="hidden sm:inline">•</span>
                  <span>{industry}</span>
                  <span className="hidden sm:inline">•</span>
                  <span className="hidden sm:inline">{formatDate(content?.created_at)}</span>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  onToggleFavorite(content?.id);
                }}
                className="p-2 rounded-lg hover:bg-muted transition-colors flex-shrink-0 touch-manipulation"
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Icon
                  name={isFavorite ? 'Heart' : 'Heart'}
                  size={18}
                  color={isFavorite ? 'var(--color-error)' : 'var(--color-muted-foreground)'}
                  fill={isFavorite ? 'var(--color-error)' : 'none'}
                />
              </button>
            </div>

            <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mb-2 sm:mb-3">
              {content?.content?.substring(0, 150)}...
            </p>

            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              {performanceScore > 0 && (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Icon name="TrendingUp" size={14} className={getPerformanceColor(performanceScore)} />
                  <span className={`text-xs sm:text-sm font-semibold ${getPerformanceColor(performanceScore)}`}>
                    {performanceScore}%
                  </span>
                </div>
              )}
              {sourceUrl && (
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e?.stopPropagation()}
                  className="flex items-center gap-1 text-xs sm:text-sm text-primary hover:underline"
                >
                  <Icon name="ExternalLink" size={12} />
                  Source
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-250 cursor-pointer touch-manipulation"
      onClick={() => onPreview(content)}
    >
      {/* Thumbnail */}
      <div className="relative w-full h-40 sm:h-48 bg-muted">
        <img
          src={thumbnailUrl}
          alt={`Thumbnail for ${content?.title}`}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'https://api.dicebear.com/7.x/shapes/svg?seed=' + content?.id;
          }}
        />
        <div className="absolute top-2 sm:top-3 right-2 sm:right-3">
          <button
            onClick={(e) => {
              e?.stopPropagation();
              onToggleFavorite(content?.id);
            }}
            className="p-2 rounded-lg bg-card/90 backdrop-blur-sm hover:bg-card transition-colors touch-manipulation"
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Icon
              name={isFavorite ? 'Heart' : 'Heart'}
              size={16}
              color={isFavorite ? 'var(--color-error)' : 'var(--color-muted-foreground)'}
              fill={isFavorite ? 'var(--color-error)' : 'none'}
            />
          </button>
        </div>
        {performanceScore > 0 && (
          <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3">
            <div className={`px-2 sm:px-3 py-1 rounded-full bg-card/90 backdrop-blur-sm flex items-center gap-1 sm:gap-1.5 ${getPerformanceColor(performanceScore)}`}>
              <Icon name="TrendingUp" size={12} className="sm:w-[14px] sm:h-[14px]" />
              <span className="text-xs sm:text-sm font-semibold">{performanceScore}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Content Info */}
      <div className="p-3 sm:p-4">
        <div className="flex items-start gap-2 mb-2">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name={getContentIcon(content?.content_type)} size={14} color="var(--color-primary)" className="sm:w-4 sm:h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm sm:text-base text-foreground truncate">{content?.title}</h3>
            <p className="text-xs text-muted-foreground truncate">
              {content?.content_type?.replace('_', ' ')} • {industry}
            </p>
          </div>
        </div>

        <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mb-2 sm:mb-3">
          {content?.content?.substring(0, 100)}...
        </p>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="truncate">{formatDate(content?.created_at)}</span>
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e?.stopPropagation()}
              className="flex items-center gap-1 text-primary hover:underline flex-shrink-0 ml-2"
            >
              <Icon name="ExternalLink" size={10} className="sm:w-3 sm:h-3" />
              <span className="hidden sm:inline">Source</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentCard;